import React from "react";
import useEmblaCarousel from "embla-carousel-react";
import { EmblaOptionsType } from "embla-carousel";
import "./styles.less";
import { RightArrowIcon } from "shared-components/Components/CustomIcons/RightArrowIcon/web";
import { LeftArrowIcon } from "shared-components/Components/CustomIcons/LeftArrowIcon/web";
import { NextButton, PrevButton, usePrevNextButtons } from "./EmblaArrows";
import { usePagination } from "./EmblaPagination";
import Link from "next/link";

type NewEmblaOptionsType = EmblaOptionsType & {
	slidesPerView?: number;
	navigation?: boolean;
	pagination?: boolean;
};

type EmblaTypes = {
	options?: NewEmblaOptionsType;
	children: React.ReactNode;
	title: string;
	className?: string;
	totalSlides?: number;
	link?: string;
	target?: string;
	rel?: string;
	widthSlide?: string;
	fullWidth?: boolean;
	hasflag?: boolean;
	handleClick?: React.MouseEventHandler<HTMLAnchorElement>;
};


export const EmblaCarousel = ({
	options = {
		inViewThreshold: 0, // opcional: 0–1 suele ser lo esperado
	},
	children,
	title,
	className,
}: EmblaTypes) => {
	const [emblaRef, emblaApi] = useEmblaCarousel({
		axis: "x",
		direction: "ltr",
		containScroll: "trimSnaps",
		watchDrag: true,     // <– habilita drag (mouse/touch)
		watchResize: true,
		watchSlides: true,
		watchFocus: true,
		...options,
	});

	const {
		onPrevButtonClick,
		onNextButtonClick,
		prevBtnDisabled,
		nextBtnDisabled,
	} = usePrevNextButtons(emblaApi);

	// Evita que un click navegue si realmente fue un swipe/drag
	const handleClickCapture: React.MouseEventHandler<HTMLDivElement> = (e) => {
		if (emblaApi && !emblaApi.clickAllowed()) {
			e.preventDefault();
			e.stopPropagation();
		}
	};

	// Si quieres controlar # de slides por vista desde props (options.slidesPerView)
	// calcula el tamaño y pásalo como CSS variable:
	const slideSize =
		(options as any)?.slidesPerView
			? `${100 / (options as any).slidesPerView}%`
			: undefined;

	// Para pasar CSS custom properties en TS:
	const styleVars: React.CSSProperties & { ["--slide-size"]?: string } = {};
	if (slideSize) styleVars["--slide-size"] = slideSize;

	return (
		<section className={`embla emblaCarousel ${className ?? ""}`} style={styleVars}>
			{options.navigation && (
				<button
					className="embla__button emblaCarousel__navigation emblaCarousel__navigation--left"
					disabled={prevBtnDisabled}
					onClick={onPrevButtonClick}
				>
					<LeftArrowIcon className="icon-arrow" />
				</button>
			)}

			{/* Viewport: aquí va el ref */}
			<div className="embla__viewport" ref={emblaRef} onClickCapture={handleClickCapture}>
				<div className="embla__container">
					{React.Children.map(children, (child, index) => (
						<div
							key={`embla__slide-${title}-${index}`}
							className="embla__slide" // el ancho lo marca --slide-size (CSS)
						>
							{child}
						</div>
					))}
				</div>
			</div>

			{options.navigation && (
				<button
					className="embla__button emblaCarousel__navigation emblaCarousel__navigation--right"
					disabled={nextBtnDisabled}
					onClick={onNextButtonClick}
				>
					<RightArrowIcon className="icon-arrow" />
				</button>
			)}
		</section>
	);
};


export const EmblaGalleryCarousel = ({
	options = {},
	children,
	title,
	className,
	totalSlides,
	link,
	target,
	rel,
	hasflag = false,
	handleClick
}: EmblaTypes) => {
	const [emblaRef, emblaApi] = useEmblaCarousel(options);
	const [renderedSlides, setRenderedSlides] = React.useState<Set<number>>(new Set([0]));
	const hasInteractedRef = React.useRef<boolean>(false);
	const isInitializedRef = React.useRef<boolean>(false);
	const initTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

	const {
		onPrevButtonClick,
		onNextButtonClick,
		nextBtnDisabled,
		prevBtnDisabled,
	} = usePrevNextButtons(emblaApi);
	const { selectedIndex, onDotButtonClick } = usePagination(emblaApi);

	const childrenArray = React.Children.toArray(children);
	const childCount = childrenArray.length;

	React.useEffect(() => {
		if (!emblaApi) return;

		// Función para cargar solo la imagen visible (modo inicial)
		const loadSingleSlide = (currentIndex: number) => {
			setRenderedSlides(prev => {
				const newRendered = new Set(prev);
				newRendered.add(currentIndex);
				return newRendered;
			});
		};

		// Función para cargar chunk de 5 imágenes (modo interactivo)
		// Carga: 2 anteriores, actual, 2 siguientes
		const loadChunkOfFive = (currentIndex: number) => {
			setRenderedSlides(prev => {
				const newRendered = new Set(prev);
				
				// Add current slide
				newRendered.add(currentIndex);
				
				// Add 2 previous slides for preloading
				const prevIndex1 = (currentIndex - 1 + childCount) % childCount;
				const prevIndex2 = (currentIndex - 2 + childCount) % childCount;
				
				// Add 2 next slides for preloading
				const nextIndex1 = (currentIndex + 1) % childCount;
				const nextIndex2 = (currentIndex + 2) % childCount;
				
				newRendered.add(prevIndex1);
				newRendered.add(prevIndex2);
				newRendered.add(nextIndex1);
				newRendered.add(nextIndex2);
				
				return newRendered;
			});
		};

		// Carga inicial: solo 1 imagen visible
		const initializeSlides = () => {
			const currentIndex = emblaApi.selectedScrollSnap();
			hasInteractedRef.current = false;
			isInitializedRef.current = false;
			loadSingleSlide(currentIndex);
			
			// Limpiar timeout anterior si existe
			if (initTimeoutRef.current) {
				clearTimeout(initTimeoutRef.current);
			}
			
			// Marcar como inicializado después de un pequeño delay para evitar eventos 'select' iniciales
			// Esto permite distinguir entre eventos automáticos de inicialización y interacciones del usuario
			initTimeoutRef.current = setTimeout(() => {
				isInitializedRef.current = true;
			}, 100);
		};

		// Manejo de navegación del usuario
		const handleSlideSelect = () => {
			const currentIndex = emblaApi.selectedScrollSnap();
			
			if (!isInitializedRef.current) {
				loadSingleSlide(currentIndex);
				return;
			}

			if (hasInteractedRef.current) {
				loadChunkOfFive(currentIndex);
			} else {
				hasInteractedRef.current = true;
				loadChunkOfFive(currentIndex);
			}
		};

		// Inicialización: solo carga 1 imagen
		initializeSlides();
		
		// Escuchar eventos de navegación del usuario
		emblaApi.on('select', handleSlideSelect);
		emblaApi.on('reInit', initializeSlides);

		return () => {
			if (initTimeoutRef.current) {
				clearTimeout(initTimeoutRef.current);
			}
			emblaApi.off('select', handleSlideSelect);
			emblaApi.off('reInit', initializeSlides);
		};
	}, [emblaApi, childCount]);

	return (
		<section className={`emblaGalleryCarousel ${className ?? ''}`}>
			{options.navigation && totalSlides > 1 && (
				<PrevButton
					onClick={() => {
						onPrevButtonClick();
						onDotButtonClick(selectedIndex - 1);
					}}
					disabled={prevBtnDisabled}
				/>
			)}

			<Link href={link} passHref>
				<a target={target} rel={rel} onClick={handleClick}>
					<div className="emblaGalleryCarousel__viewport" ref={emblaRef}>
						<div className="emblaGalleryCarousel__container">
							{childrenArray.map((child, index) => (
								<div
									key={`emblaGallery__slide-${title}-${index}`}
									className="emblaGalleryCarousel__slide">
									{renderedSlides.has(index) ? child : <div style={{ minHeight: '200px' }} />}
								</div>
							))}
						</div>
					</div>
				</a>
			</Link>

			{options.navigation && totalSlides > 1 && (
				<NextButton
					onClick={() => {
						onNextButtonClick();
						onDotButtonClick(selectedIndex + 1);
					}}
					disabled={nextBtnDisabled}
				/>
			)}

			{options.pagination && (
				<div className={`emblaGalleryCarousel__pagination ${hasflag ? 'has-flag' : ''}`}>
					<span>
						{selectedIndex + 1} / {totalSlides}
					</span>
				</div>
			)}
		</section>
	);
};

export const EmblaCarouselHome = ({
	options = {
		inViewThreshold: Infinity,
	},
	children,
	title,
	className,
	widthSlide,
	fullWidth
}: EmblaTypes) => {
	const [emblaRef, emblaApi] = useEmblaCarousel(options);
	const {
		onPrevButtonClick,
		onNextButtonClick,
		nextBtnDisabled,
		prevBtnDisabled,
	} = usePrevNextButtons(emblaApi);

	return (
		<div className={`emblaCarouselHome ${className ? className : ''}`}>
			{options.navigation && (
				<button
					className="emblaCarouselHome__navigation emblaCarouselHome__navigation--left embla__button"
					disabled={prevBtnDisabled}
					onClick={onPrevButtonClick}>
					<LeftArrowIcon className="icon-arrow" />
				</button>
			)}
			<div className={`embla ${fullWidth ? 'emblaFullWidth' : ''}`} ref={emblaRef}>
				<div className="embla__container">
					{React.Children.map(children, (child, index) => (
						<div
							key={`embla__slide-${title}-${index}`}
							className="embla__slide"
							style={{ flex: `0 0 ${widthSlide ? widthSlide : "auto"}` }}>
							{child}
						</div>
					))}
				</div>
			</div>
			{options.navigation && (
				<button
					className="emblaCarouselHome__navigation emblaCarouselHome__navigation--right embla__button"
					disabled={nextBtnDisabled}
					onClick={onNextButtonClick}>
					<RightArrowIcon className="icon-arrow" />
				</button>
			)}
		</div>
	);
};

